import { useEffect, useRef, type RefObject, type TouchEvent } from 'react'
import { swipeAllowedOnVideo, swipeDirection } from './swipe'
import { isTopLayer } from './useBack'

/** Поле, куда печатают: там F и D — буквы, а не команды. Галочки не считаются. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return true
  if (target.tagName !== 'INPUT') return false
  const type = (target as HTMLInputElement).type
  return type !== 'checkbox' && type !== 'radio' && type !== 'button'
}

interface CardKeys {
  prev: () => void
  next: () => void
  download?: (() => void) | null
  /** Esc, когда карточка — сам экран (группа избранного). Над лентой Esc закрывает стек «Назад». */
  close?: () => void
}

/**
 * Одни клавиши для карточки над лентой и для группы избранного — на компьютере
 * и в Telegram Desktop: ← → листают, D скачивает, Esc закрывает. F — у ♥
 * (FavoriteButton). Пока поверх открыто окно или лист, клавиши его, а не карточки.
 */
export function useCardKeys(layer: number, keys: CardKeys): void {
  const ref = useRef(keys)
  useEffect(() => {
    ref.current = keys
  })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return
      if (!isTopLayer(layer)) return
      const k = ref.current
      if (e.key === 'ArrowLeft') k.prev()
      else if (e.key === 'ArrowRight') k.next()
      // code, а не key: D работает и в русской раскладке
      else if (e.code === 'KeyD' && k.download && !e.repeat) k.download()
      else if (e.key === 'Escape' && k.close) k.close()
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [layer])
}

/**
 * Свайп влево-вправо — соседний пост, и по видео тоже, кроме полосы с кнопками
 * плеера внизу. Вертикальный жест оставлен прокрутке и Telegram.
 */
export function useSwipe(go: (direction: 'next' | 'prev') => void) {
  const start = useRef<{ x: number; y: number } | null>(null)
  return {
    onTouchStart: (e: TouchEvent) => {
      const t = e.touches[0]
      start.current = null
      if (!t || e.touches.length > 1) return
      const target = e.target as HTMLElement
      if (target.closest('input, textarea, select, [data-noswipe]')) return
      const video = target.closest('video')
      if (video && !swipeAllowedOnVideo(t.clientY, video.getBoundingClientRect().bottom)) return
      start.current = { x: t.clientX, y: t.clientY }
    },
    onTouchEnd: (e: TouchEvent) => {
      const from = start.current
      start.current = null
      const t = e.changedTouches[0]
      if (!from || !t) return
      const direction = swipeDirection(from, { x: t.clientX, y: t.clientY })
      if (direction) go(direction)
    },
  }
}

/** Перемотать плеер и показать его: на телефоне он выше транскрипта и раскадровки. */
export function seekVideo(videoRef: RefObject<HTMLVideoElement | null>, t: number): void {
  const v = videoRef.current
  if (!v) return
  v.currentTime = t
  void v.play().catch(() => undefined)
  const box = v.closest<HTMLElement>('[data-media]') ?? v
  box.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
}
